import {
  FilterDocument,
  Goal,
  GoalProgressCount,
  Habit,
  ID,
  LifeArea,
  Relationship,
  RepeatingTask,
  Tag,
  Task,
  TaskFilterOptions,
  Timestamp,
  User,
} from '@griply/types';
import {
  buildEntityLinks,
  buildGoalOpenTaskCounts,
  buildGoalProgressCounts,
  countMatchingItems,
  FilterSlices,
  getFilterDocumentOptions,
  getWeekStartsOn,
  isTaskTypeTask,
  todayFilterOptions,
} from '@griply/utils';

export type TaskCountsDocument = {
  id: ID;
  document: FilterDocument;
};

export type DeriveTaskCountsContext = {
  todayDocument: FilterDocument;
  inboxDocument: FilterDocument;
  smartListDocuments: TaskCountsDocument[];
  tagDocuments: TaskCountsDocument[];
  goals: Goal[];
  tags: Tag[];
  relationships: Relationship[];
  lifeAreas: LifeArea[];
  slices: FilterSlices | undefined;
  user: User | null | undefined;
  today: Timestamp;
};

export type DeriveTaskCountsInput = DeriveTaskCountsContext & {
  activeTasks: Task[];
  completedTasks: Task[];
  habits: Habit[];
  repeatingTasks: RepeatingTask[];
  goalProgressTasks: Task[];
};

export type TaskCounts = {
  today: number;
  inbox: number;
  bySmartListId: Map<ID, number>;
  byTagId: Map<ID, number>;
  byGoalId: Map<ID, number>;
  byGoalProgress: Map<ID, GoalProgressCount>;
};

type DocumentFilterOptions = {
  includeTaskWithAction?: boolean;
  excludeHiddenTodoHabits?: boolean;
};

const getDocumentFilters = (
  context: DeriveTaskCountsContext,
  filterDocument: FilterDocument,
  options: DocumentFilterOptions = {},
): TaskFilterOptions[] =>
  getFilterDocumentOptions({
    filterDocument,
    goals: context.goals,
    tags: context.tags,
    relationships: context.relationships,
    lifeAreas: context.lifeAreas,
    user: context.user,
    today: context.today,
    slices: context.slices,
    ...options,
  });

// Does any nav document surface completed tasks? Computed before counts so the
// resolver can decide whether to enable the (lazy) completed-tasks subscription
// without first fetching it.
export const deriveAnyDocumentShowsCompleted = (
  context: DeriveTaskCountsContext,
): boolean => {
  const documentFilters = [
    getDocumentFilters(context, context.todayDocument, todayFilterOptions),
    getDocumentFilters(context, context.inboxDocument),
    ...context.smartListDocuments.map(({ document }) =>
      getDocumentFilters(context, document),
    ),
    ...context.tagDocuments.map(({ document }) =>
      getDocumentFilters(context, document),
    ),
  ];

  return documentFilters.some((filters) =>
    filters.some((filter) => !!filter.showCompleted),
  );
};

export const deriveTaskCounts = (input: DeriveTaskCountsInput): TaskCounts => {
  const {
    activeTasks,
    completedTasks,
    habits,
    repeatingTasks,
    goalProgressTasks,
    goals,
    todayDocument,
    inboxDocument,
    smartListDocuments,
    tagDocuments,
    relationships,
    user,
    today,
  } = input;

  const weekStartsOn = getWeekStartsOn(user);
  const taskDocs = [...activeTasks, ...completedTasks].filter(isTaskTypeTask);

  const countFor = (filters: TaskFilterOptions[]): number =>
    countMatchingItems({
      filters,
      tasks: taskDocs,
      habits,
      repeatingTasks,
      weekStartsOn,
      today,
    });

  const bySmartListId = new Map<ID, number>();
  smartListDocuments.forEach(({ id, document }) => {
    bySmartListId.set(id, countFor(getDocumentFilters(input, document)));
  });

  const byTagId = new Map<ID, number>();
  tagDocuments.forEach(({ id, document }) => {
    byTagId.set(id, countFor(getDocumentFilters(input, document)));
  });

  return {
    today: countFor(
      getDocumentFilters(input, todayDocument, todayFilterOptions),
    ),
    inbox: countFor(getDocumentFilters(input, inboxDocument)),
    bySmartListId,
    byTagId,
    byGoalId: buildGoalOpenTaskCounts(activeTasks),
    byGoalProgress: buildGoalProgressCounts(goalProgressTasks, goals, {
      links: buildEntityLinks(relationships),
    }),
  };
};
